import * as React from "react";
import { useContext, useState } from "react";
import { Button, Checkbox, Intent, Position, Tooltip } from "@blueprintjs/core";

import { GenomeDataset, GenomesBulkDownloadQueryResult, RestCallResult } from "../../../api";
import { StoresContext } from "../../../pages/_app";
import { AppToaster } from "../../toaster";
import { Popover2 } from "@blueprintjs/popover2";
import { datetimeFormatter } from "../../../utils/dates";
import { prettyFileSize } from "../../../utils/size";
import { observer } from "mobx-react";
import RootStore from "../../../stores";
import { RESTRICTED_COUNTRY_DOWNLOAD_ERROR_MSG } from "../../../utils/constants";

export type DownloadType = "assembly" | "annotations" | GenomeDataset["type"];

export type DownloadInfo = {
  type: DownloadType;
  created_at?: string;
  filename?: string;
  size?: number;
  num_available?: number;
};

// Sort order of download types in the menu
const ALL_DOWNLOAD_TYPES: DownloadType[] = [
  "assembly",
  "annotations",
  "exome",
  "transcriptome",
  "epigenome",
  "resistome",
  "supplemental_data",
];

const DOWNLOAD_TYPE_TO_LABEL: Record<DownloadType, string> = {
  assembly: "Assembly Data",
  annotations: "Annotations Data",
  exome: "WES Data",
  transcriptome: "RNASeq Data",
  epigenome: "Methylation Data",
  resistome: "Resistome Data",
  supplemental_data: "Supplemental Data",
};

const DOWNLOAD_TYPE_TO_DESCRIPTION: Record<DownloadType, string[]> = {
  assembly: ["A FASTA file containing assembled genomic sequences"],
  annotations: ["A Genbank (.gbk) file containing annotations for the associated assembly"],
  exome: [
    "A JSON file containing biomaterial and Whole Exome Sequencing metadata",
    "Variants in VCF format - only highly filtered variants are provided",
  ],
  transcriptome: [
    "A JSON file containing biomaterial and RNA sequencing metadata",
    "The raw read counts, FPKM, and TPM values for each replicate",
  ],
  epigenome: [
    "A JSON file containing metadata for the Methylation experiments and the associated data in BED format",
  ],
  resistome: ["A spreadsheet of antimicrobial resistance phenotypes"],
  supplemental_data: ["Additional data associated with this genome"],
};

type Props = {
  downloadInfos: DownloadInfo[];
  initBulkDownload: (downloadTypes: DownloadType[]) => Promise<RestCallResult<unknown>>;
  initBulkSearch: (
    downloadTypes: DownloadType[]
  ) => Promise<RestCallResult<GenomesBulkDownloadQueryResult>>;
  hasRequiredSubscription?: () => boolean;
  showLotIdOption?: boolean;
  disabled?: boolean;
  large?: boolean;
};

export const DownloadSelectedButton = observer(
  ({
    downloadInfos,
    initBulkDownload,
    initBulkSearch,
    showLotIdOption = false,
    disabled = false,
    large = false,
    hasRequiredSubscription,
  }: Props) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [downloadTypes, setDownloadTypes] = useState<DownloadType[]>(
      downloadInfos.map((info) => info.type)
    );

    const rootStore = useContext(StoresContext);

    async function callInitBulkDownload() {
      setIsLoading(true);
      // Let's make a local copy of downloadTypes.
      return initBulkDownload([...downloadTypes])
        .then(({ error, statusCode }) => {
          if (error === undefined) {
            showSuccessNotification(rootStore);
            return null;
          } else {
            if (statusCode === 451) {
              return RESTRICTED_COUNTRY_DOWNLOAD_ERROR_MSG;
            } else if (rootStore.userStore.lotId) {
              return "The lot number you entered does not match the genome you are trying to download. Please check your number, or contact tech@atcc.org.";
            } else {
              return `Could not download the ${downloadTypes
                .map((x) => DOWNLOAD_TYPE_TO_LABEL[x])
                .join(", ")}, please try again later.`;
            }
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    const handleDownloadClicked = () => {
      setIsOpen(false);
      rootStore.userStore.tryToDoGuardedAction(
        callInitBulkDownload,
        showLotIdOption,
        hasRequiredSubscription
      );
    };

    if (downloadInfos.length === 0) {
      return null;
    }

    let button = (
      <Button
        className="atcc--uppercase"
        intent={disabled ? Intent.NONE : Intent.PRIMARY}
        onClick={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
        text="Download Data"
        icon="download"
        loading={isLoading}
        disabled={disabled}
        large={large}
      />
    );
    if (disabled) {
      button = (
        <Tooltip
          content={
            <span>
              Select one or more products to download data.
              {rootStore.userStore.hasActiveSubscription() ? null : (
                <>
                  <br />
                  An ATCC Supporting Membership is required to download multiple products.
                </>
              )}
            </span>
          }
          position={Position.RIGHT_BOTTOM}
        >
          {button}
        </Tooltip>
      );
    }

    return (
      <Popover2
        placement="bottom-start"
        popoverClassName="download-selected-button-popover"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        content={
          <DownloadSelectedPopoverContent
            isLoading={isLoading}
            downloadInfos={downloadInfos}
            downloadTypes={downloadTypes}
            initBulkGenomeSearch={initBulkSearch}
            onChange={setDownloadTypes}
            onDownload={handleDownloadClicked}
          />
        }
      >
        {button}
      </Popover2>
    );
  }
);

export function showSuccessNotification(rootStore: RootStore) {
  if (AppToaster) {
    const email = rootStore.userStore.getUserEmail()!;
    AppToaster.show({
      message: `We are preparing your download. We will email a link to you (${email}) once your download is ready. Time to generate downloads may vary, depending on number of genomes selected and file sizes.`,
      intent: Intent.PRIMARY,
      timeout: 0, // disables the timeout
    });
  }
}

export const DownloadSelectedPopoverContent = ({
  downloadInfos,
  downloadTypes,
  initSingleGenomeSearch,
  initBulkGenomeSearch,
  uuid,
  isLoading,
  onChange,
  onDownload,
}: {
  isLoading: boolean;
  downloadTypes: DownloadType[];
  downloadInfos: DownloadInfo[];
  initSingleGenomeSearch?: (
    uuid: string,
    downloadTypes: DownloadType[]
  ) => Promise<RestCallResult<GenomesBulkDownloadQueryResult>>;
  initBulkGenomeSearch?: (
    downloadTypes: DownloadType[]
  ) => Promise<RestCallResult<GenomesBulkDownloadQueryResult>>;
  uuid?: string;
  onChange: (newDownloadTypes: DownloadType[]) => void;
  onDownload: () => void;
}) => {
  const [searchResults, setSearchResults] =
    useState<RestCallResult<GenomesBulkDownloadQueryResult> | null>(null);

  // fetch counts for all download types so that the counts are displayed regardless if the box
  // is checked or not
  React.useEffect(() => {
    const fetchResults = async () => {
      let result;
      if (uuid) {
        result = await initSingleGenomeSearch!(uuid, ALL_DOWNLOAD_TYPES);
      } else {
        result = await initBulkGenomeSearch!(ALL_DOWNLOAD_TYPES);
      }
      setSearchResults(result);
    };
    fetchResults();
  }, []);

  const sortedDownloadInfos = [...downloadInfos];
  sortedDownloadInfos.sort(
    (a, b) => ALL_DOWNLOAD_TYPES.indexOf(a.type) - ALL_DOWNLOAD_TYPES.indexOf(b.type)
  );

  const sortedDownloadInfosWithCounts = sortedDownloadInfos.map((info) => ({
    ...info,
    num_available: searchResults?.data?.dataset_counts_by_type?.[info.type],
  }));

  const innerButtonDisabled = downloadTypes.length === 0 || isLoading;

  return (
    <div className="bulk-download-menu">
      {sortedDownloadInfosWithCounts.map((info) => (
        <Checkbox
          key={info.type}
          disabled={isLoading || info.num_available === 0}
          checked={!!info.num_available && downloadTypes.indexOf(info.type) !== -1}
          onClick={() => {
            if (downloadTypes.indexOf(info.type) === -1) {
              onChange([...downloadTypes, info.type]);
            } else {
              onChange(downloadTypes.filter((x) => x !== info.type));
            }
          }}
        >
          <CheckboxLabel downloadInfo={info} />
        </Checkbox>
      ))}
      <div className="bulk-download-menu__button-place">
        <Button
          loading={isLoading}
          intent={innerButtonDisabled ? Intent.NONE : Intent.PRIMARY}
          text="Download"
          disabled={innerButtonDisabled}
          onClick={onDownload}
          large
        />
      </div>
    </div>
  );
};

function CheckboxLabel({ downloadInfo }: { downloadInfo: DownloadInfo }) {
  const filename = downloadInfo.filename ? (
    <span className="bulk-download-menu__checkbox-label__info__filename">
      {downloadInfo.filename}
    </span>
  ) : null;
  const size =
    downloadInfo.size !== undefined ? (
      <div className="bulk-download-menu__checkbox-label__info__size">
        {prettyFileSize(downloadInfo.size)}
      </div>
    ) : null;
  const createdAt = downloadInfo.created_at ? (
    <div>{datetimeFormatter(downloadInfo.created_at)}</div>
  ) : null;
  const datasetsAvailable =
    downloadInfo.num_available != null ? (
      <span className="bulk-download-menu__checkbox-label__info__count">
        {" "}
        ({downloadInfo.num_available} dataset{downloadInfo.num_available !== 1 ? "s" : ""}{" "}
        available)
      </span>
    ) : null;

  return (
    <div className="bulk-download-menu__checkbox-label">
      <div className="bulk-download-menu__checkbox-label__info">
        <span className="bulk-download-menu__checkbox-label__info__name">
          {DOWNLOAD_TYPE_TO_LABEL[downloadInfo.type]}
        </span>
        {datasetsAvailable}
        <div>{filename}</div>
        {createdAt}
      </div>
      {size}
      <DownloadDescription type={downloadInfo.type} />
    </div>
  );
}

function DownloadDescription({ type }: { type: DownloadType }) {
  const description = DOWNLOAD_TYPE_TO_DESCRIPTION[type];
  let content;
  if (description.length > 1) {
    content = (
      <ul>
        {description.map((item, idx) => (
          <li key={`${type}-item-${idx}`}>{item}</li>
        ))}
      </ul>
    );
  } else {
    content = <>{description[0]}</>;
  }
  return <div className="bulk-download-menu__checkbox-label__description">{content}</div>;
}
